/**
 * ConnectionTab - Compact connection management UI
 *
 * A sleek, compact component that shows:
 * - Current connection status with left border accent
 * - Quick actions (test)
 * - Inline connection picker
 */

import { useState, useMemo, useCallback, useEffect, useId, useRef } from 'react';
import {
  makeStyles,
  tokens,
  Text,
  Button,
  Input,
  Spinner,
  Badge,
  MessageBar,
  MessageBarBody,
  MessageBarActions,
  mergeClasses,
} from '@coreai/fluentui-react';
import { useQueryClient } from '@tanstack/react-query';
import {
  CheckmarkCircle16Filled,
  DismissCircle16Filled,
  Warning16Filled,
  PlugDisconnected16Regular,
  Search16Regular,
  Add16Regular,
  ArrowSync16Regular,
  Database20Regular,
} from '@fluentui/react-icons';
import { useNodeEditorStore } from '@/stores/nodeEditorStore';
import { useConnectionStore } from '@/stores/connectionStore';
import { useNodeConnectionStatus } from '@/hooks/useNodeConnectionStatus';
import { isConnectionReferenceComplete } from '@/services/connectionService';
import { useTestConnection, useConnections, useRegisterCreatedConnection, connectionKeys } from '@/hooks/useConnectionMutations';
import { useConnectionsMeta } from '@/hooks/useConnectionsList';
import { useNodeOperationInfo } from '@/hooks/useNodeOperationInfo';
import { useOperationManifest } from '@/hooks/useOperationManifest';
import { useServiceProviderConnector } from '@/hooks/useServiceProviderConnector';
import { useGetConnectorInfo } from '@/contexts';
import { actionNameKey } from '@/hooks/useWorkflowConnectors';
import { extractConnectorName, isAgentConnectionId, isHostedAgentConnectionId, isHostedAgentConnectionReference, isManagedApiConnector, isServiceProviderConnector, resolveOAuthConnectionStatus, getValidConnections, resolveCreationConnectorName, mapApiConnectionsToMerged, classifyMergedConnectionType, filterConnectionsBySearch, buildManagedModelReference, MANAGED_MODEL_REFERENCE_KEY, MANAGED_MODEL_DISPLAY_NAME, MANAGED_MODEL_PROVIDER_ID, type MergedConnection } from '@/utils/connections';
import { useOAuthConnectionParameterKeys } from '@/hooks/useOAuthConnectionParameterKeys';
import { FOUNDRY_AGENT_PROVIDERS, getAgentReferenceProvider, getAllowedAgentProviders, readAgentModelType, readAgentHarnessType, type AgentModelProvider } from '../../workflowHelpers';
import { buildConnectorManifest } from '@/services/manifestConnectionService';
import { ConnectionCreationPanel } from '../dialogs/ConnectionCreationPanel';
import { CompactConnectionItem } from '@/components/common/CompactConnectionItem';
import { TablePaginationFooter, usePagination } from '@/components/shared/DataTable';
import type { ConnectionReference} from '@/types/connectionReference';
import type { AgentModelType } from '@/types/agentConnection';
import { getAgentConnectionCreationModelType, getAgentVariantFromTypes } from '../../helpers/agentVariant';
import { useManagedModels } from '@/hooks/useManagedModels';
import { useCurrentProject } from '@/hooks/useCurrentProject';

/**
 * Map an AgentModelProvider (connection-level) to the corresponding AgentModelType
 * (action parameter) for the *live UI state*. Most values are identical, but
 * 'OpenAI' connections use 'V1ChatCompletionsService'. (GitHubCopilot stays as-is
 * here — the serializer rewrites it to V1ChatCompletionsService at save time.)
 */
const providerToModelType = (provider: AgentModelProvider): AgentModelType => {
  if (provider === 'OpenAI') return 'V1ChatCompletionsService';
  return provider as AgentModelType;
};

// localStorage key for the picker's page size, matching AppList / WorkflowList's
// per-list persistence pattern (`otto.appList.pageSize`, `otto.workflowList.pageSize`).
const PICKER_PAGE_SIZE_STORAGE_KEY = 'otto.connectionPicker.pageSize';
// Rows the connection list scrolls internally before pagination kicks in — the
// picker sits inside a slim drawer, so we cap the viewport rather than let the
// full 25-row page push other sections off-screen.
const LIST_VIEWPORT_ROWS = 8;
// PageUp / PageDown keyboard step (in rows) inside the current pagination page,
// scaled to the visible viewport so a single press moves ~one screenful.
const KEYBOARD_PAGE_STEP = LIST_VIEWPORT_ROWS;

const useStyles = makeStyles({
  container: {
    display: 'flex',
    flexDirection: 'column',
    gap: tokens.spacingVerticalM,
    flex: 1,
    minHeight: 0,
  },

  // Current connection card - compact with left border accent
  currentCard: {
    backgroundColor: tokens.colorNeutralBackground1,
    borderRadius: tokens.borderRadiusMedium,
    border: `1px solid ${tokens.colorNeutralStroke2}`,
    borderLeftWidth: '3px',
    borderLeftStyle: 'solid',
    borderLeftColor: tokens.colorNeutralStroke2,
  },
  currentCardConnected: {
    borderLeftColor: tokens.colorPaletteGreenBorder1,
  },
  currentCardError: {
    borderLeftColor: tokens.colorPaletteRedBorder1,
  },
  currentCardWarning: {
    borderLeftColor: tokens.colorPaletteYellowBorder1,
  },
  currentCardMock: {
    backgroundColor: tokens.colorPaletteBlueBackground2,
    borderTopColor: tokens.colorPaletteBlueBorderActive,
    borderRightColor: tokens.colorPaletteBlueBorderActive,
    borderBottomColor: tokens.colorPaletteBlueBorderActive,
    borderLeftColor: tokens.colorPaletteBlueBorderActive,
  },

  // Card header - compact, no colored background
  cardHeader: {
    display: 'flex',
    alignItems: 'center',
    gap: tokens.spacingHorizontalS,
    padding: `${tokens.spacingVerticalS} ${tokens.spacingHorizontalM}`,
  },

  statusIcon: {
    fontSize: tokens.fontSizeBase500,
    flexShrink: 0,
  },
  statusIconConnected: {
    color: tokens.colorPaletteGreenForeground1,
  },
  statusIconError: {
    color: tokens.colorPaletteRedForeground1,
  },
  statusIconWarning: {
    color: tokens.colorPaletteYellowForeground1,
  },
  statusIconNone: {
    color: tokens.colorNeutralForeground3,
  },
  statusIconMock: {
    color: tokens.colorPaletteBlueForeground2,
  },

  statusInfo: {
    flex: 1,
    minWidth: 0,
    display: 'flex',
    flexDirection: 'column',
    gap: tokens.spacingVerticalXXS,
  },
  statusTitle: {
    fontSize: tokens.fontSizeBase300,
    fontWeight: tokens.fontWeightMedium,
    color: tokens.colorNeutralForeground1,
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
  },
  statusSubtitle: {
    fontSize: tokens.fontSizeBase200,
    color: tokens.colorNeutralForeground3,
  },

  // Actions inline with header
  headerActions: {
    display: 'flex',
    alignItems: 'center',
    gap: tokens.spacingHorizontalXS,
    flexShrink: 0,
  },

  // Connection list section
  listSection: {
    display: 'flex',
    flexDirection: 'column',
    gap: tokens.spacingVerticalS,
  },
  listHeader: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
  },
  listTitle: {
    fontSize: tokens.fontSizeBase200,
    fontWeight: tokens.fontWeightSemibold,
    color: tokens.colorNeutralForeground2,
    textTransform: 'uppercase',
    letterSpacing: '0.5px',
  },

  // Search
  searchInput: {
    width: '100%',
  },

  // Paginated listbox — a plain scroll container that renders the current page
  // of rows (usePagination slices to pageSize). Scrolls internally when the
  // rendered page exceeds the viewport height, so a big page can't push the
  // pagination footer off-screen. Also the single tab stop for the shared
  // keyboard model (`aria-activedescendant` cursor over the rendered options).
  connectionList: {
    display: 'flex',
    flexDirection: 'column',
    gap: tokens.spacingVerticalXXS,
    maxHeight: `calc(48px * ${LIST_VIEWPORT_ROWS})`,
    overflowY: 'auto',
    borderRadius: tokens.borderRadiusMedium,
    ':focus-visible': {
      outlineStyle: 'solid',
      outlineWidth: '2px',
      outlineColor: tokens.colorBrandStroke1,
      outlineOffset: '2px',
    },
  },
  // Compact wrapper for TablePaginationFooter — bounds it to the picker's
  // column so the "Showing 1–25 of N · Page A of B ◀ ▶" line wraps sensibly
  // in the narrow drawer instead of blowing out to fill horizontally.
  paginationFooter: {
    minHeight: 0,
  },
  // Active-descendant highlight. Only an outline (no background) so it composes
  // cleanly with the selected/bound state's brand fill instead of overriding it
  // — a row that is both selected and keyboard-active shows both cues.
  itemActive: {
    outlineStyle: 'solid',
    outlineWidth: '2px',
    outlineColor: tokens.colorBrandStroke1,
    outlineOffset: '-2px',
  },
  // Error state for a failed connection list fetch.
  errorContainer: {
    display: 'flex',
    flexDirection: 'column',
    gap: tokens.spacingVerticalS,
    padding: tokens.spacingVerticalS,
  },

  // Create new button - compact single line
  createNewButton: {
    display: 'flex',
    alignItems: 'center',
    gap: tokens.spacingHorizontalS,
    padding: `${tokens.spacingVerticalXS} ${tokens.spacingHorizontalS}`,
    borderRadius: tokens.borderRadiusMedium,
    cursor: 'pointer',
    backgroundColor: 'transparent',
    border: `1px dashed ${tokens.colorNeutralStroke2}`,
    transitionProperty: 'background-color, border-color',
    transitionDuration: tokens.durationFast,
    ':hover': {
      backgroundColor: tokens.colorNeutralBackground3,
      border: `1px dashed ${tokens.colorBrandStroke1}`,
    },
    ':focus-visible': {
      outlineStyle: 'solid',
      outlineWidth: '2px',
      outlineColor: tokens.colorBrandStroke1,
      outlineOffset: '-2px',
    },
  },
  createNewIcon: {
    width: '24px',
    height: '24px',
    borderRadius: tokens.borderRadiusSmall,
    backgroundColor: tokens.colorBrandBackground2,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    flexShrink: 0,
    color: tokens.colorBrandForeground1,
  },
  createNewText: {
    fontSize: tokens.fontSizeBase200,
    fontWeight: tokens.fontWeightMedium,
    color: tokens.colorNeutralForeground1,
  },

  // Empty state - compact
  emptyState: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    padding: tokens.spacingVerticalL,
    textAlign: 'center',
    gap: tokens.spacingVerticalS,
  },
  emptyIcon: {
    fontSize: tokens.fontSizeBase600,
    color: tokens.colorNeutralForeground4,
  },
  emptyText: {
    fontSize: tokens.fontSizeBase200,
    color: tokens.colorNeutralForeground3,
  },

  // Loading
  loadingContainer: {
    display: 'flex',
    justifyContent: 'center',
    padding: tokens.spacingVerticalM,
  },
});

interface ConnectionTabProps {
  nodeId?: string | undefined;
  nodeType?: string | undefined;
  connectionName?: string | undefined;
}

export const ConnectionTab = ({
  nodeId: propNodeId,
  nodeType: propNodeType,
  connectionName: propConnectionName,
}: ConnectionTabProps) => {
  const styles = useStyles();
  const queryClient = useQueryClient();
  const [searchQuery, setSearchQuery] = useState('');
  const [showCreationPanel, setShowCreationPanel] = useState(false);
  const [testResult, setTestResult] = useState<'success' | 'error' | null>(null);

  // Get state from stores
  const { node, parameterValues, setConnection, setSlotConnection, setParameter } = useNodeEditorStore();
  const setNodeConnection = useConnectionStore((state) => state.setNodeConnection);
  const replaceConnectionKey = useConnectionStore((state) => state.replaceConnectionKey);
  const registerCreatedConnection = useRegisterCreatedConnection();
  const connectionReferences = useConnectionStore((state) => state.references);

  // A non-empty runtime-curated model list is the regional availability
  // signal for the synthetic "Microsoft-managed" row. The runtime enforces
  // the same list server-side, so this only controls what ConnectionTab
  // offers, never what the runtime accepts. AgentTypeView uses the same signal.
  const { projectName } = useCurrentProject();
  const { isAvailable: isManagedModelActive, defaultModelId } = useManagedModels(projectName);

  // Fetch live connections from data-plane API. `useConnections` returns
  // `AvailableConnection[]` for the rest of the app; `useConnectionsMeta`
  // exposes truncation info so the picker can surface an explicit warning
  // when the server-side `nextLink` walk hit the page cap (instead of
  // silently rendering a partial list). Note: these currently fire two
  // parallel walks — see the `useConnectionsList.ts` module docstring for
  // the merged-queryFn follow-up.
  const { data: apiConnections, isLoading: isLoadingConnections, isError: isConnectionsError, refetch: refetchConnections } = useConnections();
  const { data: connectionsMeta } = useConnectionsMeta();
  const validApiConnections = useMemo(() => getValidConnections(apiConnections ?? []), [apiConnections]);

  // Derive node info
  const nodeId = propNodeId ?? node?.id ?? '';
  const nodeType = propNodeType ?? node?.nodeData?.type;
  const isAgentOperation = nodeType === 'Agent' || nodeType === 'AgentLoop';

  // Current connection
  const currentConnectionName = propConnectionName ?? (
    isAgentOperation
      ? (parameterValues['_modelConnection_model1'] as string | undefined)
      : (parameterValues['_connection'] as string | undefined)
  );

  // A model connection bound to a hosted-agent connection (`hostedAgentConnections`,
  // api id `/hostedAgentConnections/…`) is only valid for the Foundry Hosted Agent
  // variant, which uses the dedicated HostedAgentConnectionTab. This ConnectionTab only
  // renders for non-hosted agents (native / foundry / coding), so a hosted-agent binding
  // here is a stale cross-type reference (e.g. left behind after switching an agent's
  // variant away from hosted). Treat it as no connection so it isn't shown as "Connected"
  // and the user is prompted to pick a valid one — the picked connection overwrites the
  // stale reference via handleSelectConnection.
  const boundConnectionIsHostedAgent = useMemo(() => {
    if (!isAgentOperation || !currentConnectionName) return false;
    return isHostedAgentConnectionReference(connectionReferences, currentConnectionName);
  }, [isAgentOperation, currentConnectionName, connectionReferences]);

  const effectiveConnectionName = boundConnectionIsHostedAgent ? undefined : currentConnectionName;

  // Connection status
  const { validationResult, hasConnection, connectionName: resolvedName } = useNodeConnectionStatus({
    nodeId,
    operationType: nodeType ?? '',
    connectionReferenceKey: effectiveConnectionName,
  });

  // Test mutation
  const testMutation = useTestConnection();

  // Connection status helpers
  const getStatusInfo = () => {
    if (!effectiveConnectionName) {
      return {
        status: 'none' as const,
        title: 'No connection',
        subtitle: 'Select one below',
        icon: <PlugDisconnected16Regular className={mergeClasses(styles.statusIcon, styles.statusIconNone)} />,
      };
    }

    // Check for mock-active status first (template mode with mocks enabled)
    if (validationResult?.status === 'mock-active') {
      return {
        status: 'mock' as const,
        title: 'Using Test Data',
        subtitle: 'Connection not configured',
        icon: <Database20Regular className={mergeClasses(styles.statusIcon, styles.statusIconMock)} />,
      };
    }

    if (validationResult?.isValid || hasConnection) {
      return {
        status: 'connected' as const,
        title: currentConnectionName === MANAGED_MODEL_REFERENCE_KEY
          ? MANAGED_MODEL_DISPLAY_NAME
          : (resolvedName ?? effectiveConnectionName),
        subtitle: 'Connected',
        icon: <CheckmarkCircle16Filled className={mergeClasses(styles.statusIcon, styles.statusIconConnected)} />,
      };
    }

    const displayTitle = currentConnectionName === MANAGED_MODEL_REFERENCE_KEY
      ? MANAGED_MODEL_DISPLAY_NAME
      : effectiveConnectionName;

    if (validationResult?.status === 'missing') {
      return {
        status: 'error' as const,
        title: displayTitle,
        subtitle: 'Not found',
        icon: <DismissCircle16Filled className={mergeClasses(styles.statusIcon, styles.statusIconError)} />,
      };
    }

    return {
      status: 'warning' as const,
      title: displayTitle,
      subtitle: 'Needs attention',
      icon: <Warning16Filled className={mergeClasses(styles.statusIcon, styles.statusIconWarning)} />,
    };
  };

  const statusInfo = getStatusInfo();

  // Resolve connector ID using the centralized hook (handles all node types and fallbacks)
  const { apiId: resolvedApiId, operationInfo } = useNodeOperationInfo({
    nodeData: node?.nodeData,
    parameterValues,
  });
  
  // Get connector ID for non-agent operations
  // resolvedApiId covers managed APIs (from host.apiId / connection refs)
  // operationInfo.connectorId covers service providers (from serviceProviderConfiguration)
  const connectorId = isAgentOperation ? undefined : (resolvedApiId ?? operationInfo?.connectorId);

  // Fetch operation manifest to extract connector metadata (needed for service provider connection creation)
  const { data: operationManifest } = useOperationManifest(operationInfo);

  // Fetch the full standalone connector definition. `buildConnectorManifest`
  // prefers this over the trimmed `manifest.connector` embedded in the
  // operation manifest (which drops auth-provider parameter sets).
  const { data: serviceProviderConnector } = useServiceProviderConnector(connectorId ?? null);

  const connectorManifest = useMemo(
    () =>
      buildConnectorManifest({
        connectorId,
        serviceProviderConnector,
        operationManifest,
      }),
    [connectorId, serviceProviderConnector, operationManifest]
  );

  // Get connector display info (icon, name). Lookup order:
  //   1. By managed-API name extracted from connectorId
  //   2. By the active connection reference key
  //   3. By the node's namespaced action name — useWorkflowConnectors keys the
  //      map by `action:<name>` too, so connection-less nodes resolve their
  //      connector icon / display name even though display fields aren't
  //      persisted on the action.
  const getConnectorInfo = useGetConnectorInfo();
  const apiNameForLookup = connectorId?.match(/managedApis\/([^/]+)$/)?.[1];
  const connectorInfo =
    getConnectorInfo(apiNameForLookup ?? '') ||
    getConnectorInfo(currentConnectionName ?? '') ||
    getConnectorInfo(actionNameKey(node?.name ?? ''));

  // Extract the connector name from the resolved API ID for filtering.
  // Prefer the operation manifest's connector id when available, since the
  // workflow-stored host.apiId can be a legacy/malformed value
  // (e.g. "/automationProjects/foo/managedApis/" with no connector tail).
  const resolvedConnectorName = useMemo(() => {
    const manifestConnectorId = operationManifest?.properties?.connector?.id;
    const manifestName = manifestConnectorId ? extractConnectorName(manifestConnectorId) : '';
    if (manifestName) return manifestName;
    if (!connectorId) return undefined;
    const fromConnectorId = extractConnectorName(connectorId);
    return fromConnectorId || undefined;
  }, [connectorId, operationManifest]);

  // Determine expected connection type from the node's operation type
  const isServiceProviderOperation = nodeType === 'ServiceProvider' || nodeType === 'ServiceProviderWebhook';

  // Agent model type — derived before `filteredConnections` so the agent-variant
  // filter can use it. Defaults to 'AzureOpenAI' since the downstream creation
  // panel reads this value to pick the right auth form for new connections.
  const agentModelType = useMemo(() => {
    if (!isAgentOperation) return undefined;
    const inputs = (node?.nodeData as Record<string, unknown> | undefined)?.inputs as
      | Record<string, unknown>
      | undefined;
    return readAgentModelType(parameterValues, inputs) ?? 'AzureOpenAI';
  }, [isAgentOperation, parameterValues, node]);

  // Derive the agent variant (native/foundry/coding) using the same helpers
  // the rest of the codebase uses. The variant determines whether the provider
  // selector (GHCP OAuth option) appears in the connection creation panel.
  const agentVariant = useMemo(() => {
    if (!isAgentOperation) return undefined;
    const inputs = (node?.nodeData as Record<string, unknown> | undefined)?.inputs as
      | Record<string, unknown>
      | undefined;
    const harnessType = readAgentHarnessType(parameterValues, inputs);
    return getAgentVariantFromTypes(agentModelType, harnessType);
  }, [isAgentOperation, parameterValues, node, agentModelType]);

  // Map of agent connection key/id → provider, derived from workflow-scoped
  // references. Used both to filter the list by variant and to tint each item's
  // icon (teal = native, purple = Foundry).
  const agentProviderByKey = useMemo(() => {
    const map = new Map<string, AgentModelProvider | undefined>();
    for (const [refKey, ref] of Object.entries(connectionReferences)) {
      if (!isAgentConnectionId(ref.api?.id)) continue;
      const provider = getAgentReferenceProvider(ref);
      map.set(refKey, provider);
      const connId = ref.connection?.id;
      if (connId) map.set(connId, provider);
    }
    return map;
  }, [connectionReferences]);

  const referencedConnectorIds = useMemo(
    () => Object.values(connectionReferences).map((ref) => ref.api?.id),
    [connectionReferences],
  );
  const oauthParameterKeys = useOAuthConnectionParameterKeys(referencedConnectorIds);

  // Build connection list from data-plane API + locally-created connections from the store
  const filteredConnections = useMemo(() => {
    // Bindable service-provider connections are defined in connections.json and merged in below.
    // A `/serviceProviders/*` row in the data-plane list is a credential — the OAuth connection
    // backing one of them — which survives its holder, so it must never be offered on its own.
    const bindableApiConnections = (validApiConnections ?? []).filter(
      (conn) => !isServiceProviderConnector(conn.connectorId ?? ''),
    );

    // Merge live data-plane connections with locally-created store references
    // that aren't in the API response yet. The base API mapping is shared with
    // the template picker via mapApiConnectionsToMerged.
    let connections: MergedConnection[] = mapApiConnectionsToMerged(
      bindableApiConnections,
      getConnectorInfo,
    );

    // Merge locally-created connections from the store that aren't in the API response yet
    // (e.g., service provider connections created in-memory before the draft save propagates)
    const apiKeys = new Set(connections.map((c) => c.key));
    // Dedup by underlying connection.id too: workflow references commonly use a
    // friendly alias (e.g. `serviceBus`) that differs from the ARM resource
    // name returned by the data-plane (e.g. `servicebus-1`), so a key-only
    // check would surface the same connection twice.
    const apiConnectionIds = new Set(
      connections
        .map((c) => c.connectionId?.toLowerCase())
        .filter((id): id is string => !!id),
    );
    // Precompute the case-insensitive set of available connection IDs so the
    // per-reference existence check below is O(1) instead of scanning the full
    // connection list on every iteration.
    const availableConnIds = new Set(
      (validApiConnections ?? []).map((c) => c.id.toLowerCase()),
    );
    for (const [refKey, ref] of Object.entries(connectionReferences)) {
      if (apiKeys.has(refKey)) continue;
      const refConnectionId = (ref.connection?.id ?? '').toLowerCase();
      if (refConnectionId && apiConnectionIds.has(refConnectionId)) continue;
      const apiId = ref.api?.id ?? '';
      const connType = classifyMergedConnectionType(apiId);

      const hasBackingConnection = isManagedApiConnector(apiId)
        ? availableConnIds.has(refConnectionId)
        // Service-provider and agent references have no ARM-backed connection
        // resource — `extractConnectionReferences` synthesizes
        // `connection.id = refKey` for them. That means a template-applied
        // placeholder (no `parameterValues` / `authentication`) would otherwise
        // pass through this gate and render as a valid pickable connection in
        // the SP/agent list. Require the reference to actually carry
        // configuration before we surface it.
        : isConnectionReferenceComplete(ref);

      // An OAuth-backed holder is usable only while its OAuth connection is connected, mirroring
      // how getValidConnections withholds unconnected managed connections from the picker. The
      // key belongs to the set this reference selected, not to the connector as a whole.
      const oauthParameterKey = oauthParameterKeys.getParameterKey(
        apiId,
        ref.connectionProperties?._parameterSetName as string | undefined,
      );
      // Until its own connector resolves, a service-provider reference could be OAuth-backed with
      // a dead sign-in. It is withheld for that window, but offered again if the connector fails
      // outright — never surfacing a connection the user created is the worse outcome. The wait
      // is per connector, so one slow fetch cannot empty the whole picker.
      const oauthConnectionUsable = oauthParameterKey
        ? resolveOAuthConnectionStatus(ref, apiConnections, oauthParameterKey) === 'connected'
        : !oauthParameterKeys.pending.has(apiId.toLowerCase());

      if (hasBackingConnection && oauthConnectionUsable) {
        const apiName = extractConnectorName(apiId);
        const info = getConnectorInfo(refKey) ?? getConnectorInfo(apiName);
        connections.push({
          key: refKey,
          name: ref.connectionName ?? refKey,
          type: connType,
          apiId,
          ...(ref.connection?.id ? { connectionId: ref.connection.id } : {}),
          ...(info?.iconUri ? { iconUri: info.iconUri } : {}),
        });
      }
    }

    if (import.meta.env.DEV && !resolvedConnectorName && !isAgentOperation && !isServiceProviderOperation) {
      console.warn(
        '[ConnectionTab] cannot determine connector name for managed-API node — hiding connection list to avoid surfacing unrelated connections',
        {
          nodeType,
          connectorId,
          operationManifestConnectorId: operationManifest?.properties?.connector?.id,
        },
      );
    }

    // 1. Always filter by connection type (agent / serviceProvider / managedApi)
    if (isAgentOperation) {
      // Mirror AgentTypeView: keep only agent connections whose provider
      // matches the agent's variant (coding / foundry / native). Classification
      // reads `connectionProperties` from the workflow's connection-store
      // references (the data-plane payload strips this). When no reference
      // exists for a connection (data-plane-only entry) we leave it visible so
      // users can still discover unmanaged connections.
      const allowed = getAllowedAgentProviders(agentVariant ?? 'native');
      connections = connections.filter((c) => {
        if (c.type !== 'agent') return false;
        // Never offer a hosted-agent connection on a non-hosted agent: these are classified
        // as `type: 'agent'` (isAgentConnectionId matches `/hostedAgentConnections/…`) but are
        // only valid for the Foundry Hosted Agent variant. Excluding them here prevents
        // re-introducing the cross-type binding the header guard hides.
        if (isHostedAgentConnectionId(c.apiId)) return false;
        const provider = agentProviderByKey.get(c.key) ?? (c.connectionId ? agentProviderByKey.get(c.connectionId) : undefined);
        if (provider === undefined) return true;
        return allowed.has(provider);
      });

      // Ensure the Microsoft-managed row owns its own presentation. Managed
      // model refs classify as agent
      // (`isAgentConnectionId('MicrosoftManaged') === true`) and get picked
      // up by the store-merge loop above with `name = refKey`, so an
      // existing managed-model workflow would otherwise render a row labeled
      // "managedAiConnection". Strip any pre-existing entry so the branded
      // "Microsoft-managed" row is the single, first entry.
      if (isManagedModelActive && agentVariant === 'native') {
        connections = connections.filter((c) => c.key !== MANAGED_MODEL_REFERENCE_KEY);
        connections.unshift({
          key: MANAGED_MODEL_REFERENCE_KEY,
          name: MANAGED_MODEL_DISPLAY_NAME,
          type: 'agent',
          apiId: MANAGED_MODEL_PROVIDER_ID,
          connectionId: MANAGED_MODEL_REFERENCE_KEY,
        });
      } else {
        // Region unavailable / non-native variant (foundry, coding, hosted):
        // drop the synthetic Microsoft-managed row entirely so the picker doesn't
        // offer an option the author can't actually configure. Microsoft-managed
        // is Workflow Agent (native) only — Managed Agent doesn't support it yet
        // (see applyAgentVariantDefaults), and Foundry/Hosted agentModelType is
        // fixed by the variant. The store may still seed the ref (see
        // `initializeFromWorkflow`) so stale workflow refs resolve, but the
        // picker should not surface it.
        connections = connections.filter((c) => c.key !== MANAGED_MODEL_REFERENCE_KEY);
      }
    } else if (isServiceProviderOperation) {
      connections = connections.filter((c) => c.type === 'serviceProvider');
    } else {
      connections = connections.filter((c) => c.type === 'managedApi');
    }

    // 2. Further narrow by specific connector name when available.
    // For managed-API operations we *require* a connector name — otherwise we
    // would silently show every managed connection in the app (unrelated to
    // the current operation). When the name can't be determined (e.g. the
    // node's host.apiId is malformed and the operation manifest hasn't loaded)
    // we return an empty list so the user is prompted to create a connection
    // rather than picking a wrong one.
    if (resolvedConnectorName && !isAgentOperation) {
      // Managed-API operations bind to a specific connector, so narrow the list
      // to that connector's connections. Agent operations intentionally accept
      // any provider allowed by the variant (native/coding/foundry) — filtering
      // by the connector name of the currently-selected connection would drop
      // every alternative provider (e.g. selecting a `MicrosoftManaged`
      // connection would hide all BYOM `AzureOpenAI` / `OpenAI` connections).
      connections = connections.filter((c) => {
        const name = extractConnectorName(c.apiId);
        return name.toLowerCase() === resolvedConnectorName.toLowerCase();
      });
    } else if (!resolvedConnectorName && !isAgentOperation && !isServiceProviderOperation) {
      connections = [];
    }

    if (searchQuery) {
      connections = filterConnectionsBySearch(connections, searchQuery);
    }

    return connections;
  }, [validApiConnections, connectionReferences, searchQuery, isAgentOperation, isServiceProviderOperation, resolvedConnectorName, getConnectorInfo, nodeType, connectorId, operationManifest, agentVariant, agentProviderByKey, isManagedModelActive, apiConnections, oauthParameterKeys]);

  // Handlers
  const handleSelectConnection = useCallback((referenceKey: string) => {
    if (isAgentOperation) {
      // The synthetic managed-model ref may not exist yet on first click of
      // the Microsoft-managed row (the row is prepended before the ref lands
      // in the store). Register it here so `setSlotConnection` + downstream
      // agentModelType sync see a real reference on next render.
      const isManagedModelRef = referenceKey === MANAGED_MODEL_REFERENCE_KEY;
      if (isManagedModelRef && !connectionReferences[MANAGED_MODEL_REFERENCE_KEY]) {
        registerCreatedConnection(MANAGED_MODEL_REFERENCE_KEY, buildManagedModelReference());
      }
      setSlotConnection('model1', referenceKey);
      // Sync agentModelType to match the selected connection's provider so the
      // workflow JSON stays consistent with the actual model endpoint used at runtime.
      // For the managed-model row we know the provider without a store re-read —
      // the ref may have only just been registered and won't be visible in our
      // captured `connectionReferences` closure.
      const provider = isManagedModelRef
        ? ('MicrosoftManaged' as const)
        : getAgentReferenceProvider(connectionReferences[referenceKey]);
      if (provider) {
        const modelType = providerToModelType(provider);
        if (modelType !== agentModelType) {
          setParameter('agentModelType', modelType);
        }
      }
      if (
        isManagedModelRef &&
        parameterValues['_modelConnection_model1'] !== MANAGED_MODEL_REFERENCE_KEY
      ) {
        setParameter('deploymentId', defaultModelId ?? '');
      }
    } else {
      setConnection(referenceKey);
    }
    if (nodeId) {
      setNodeConnection(nodeId, referenceKey);
    }
    setTestResult(null);
  }, [isAgentOperation, setSlotConnection, setConnection, nodeId, setNodeConnection, connectionReferences, agentModelType, parameterValues, defaultModelId, setParameter, registerCreatedConnection]);

  const handleTestConnection = useCallback(async () => {
    if (!currentConnectionName) return;
    setTestResult(null);
    try {
      const result = await testMutation.mutateAsync(currentConnectionName);
      setTestResult(result.status === 'connected' ? 'success' : 'error');
    } catch {
      setTestResult('error');
    }
  }, [currentConnectionName, testMutation]);

  const handleCreateComplete = useCallback((refKey: string, ref: ConnectionReference) => {
    const sourceRefKey = currentConnectionName;
    const existingSourceRef = sourceRefKey ? connectionReferences[sourceRefKey] : undefined;
    const sourceIsPlaceholder =
      !!sourceRefKey &&
      !!existingSourceRef &&
      !isConnectionReferenceComplete(existingSourceRef);

    if (sourceIsPlaceholder && sourceRefKey) {
      // Template placeholders should be replaced atomically so they do not
      // remain alongside the newly-created connection in connections.json.
      replaceConnectionKey(sourceRefKey, refKey, ref);
      queryClient.invalidateQueries({ queryKey: connectionKeys.lists() });
    } else {
      registerCreatedConnection(refKey, ref);
    }

    handleSelectConnection(refKey);
    // Sync agentModelType when the created connection's provider differs from
    // the current value (e.g. user picked GitHubCopilot in the provider selector
    // but the node was seeded with 'AzureOpenAI' at creation time).
    if (isAgentOperation) {
      const provider = getAgentReferenceProvider(ref);
      if (provider) {
        const modelType = providerToModelType(provider);
        if (modelType !== agentModelType) {
          setParameter('agentModelType', modelType);
        }
      }
    }
    setShowCreationPanel(false);
  }, [currentConnectionName, connectionReferences, replaceConnectionKey, queryClient, registerCreatedConnection, handleSelectConnection, isAgentOperation, agentModelType, setParameter]);

  // ---- Connection list keyboard / virtualization model ------------------
  // Compute the AI sub-variant (native vs Foundry tint) for an item — shared by
  // both the plain and virtualized render paths.
  const getAiVariant = useCallback(
    (conn: MergedConnection): 'native' | 'foundry' => {
      const provider =
        conn.type === 'agent'
          ? agentProviderByKey.get(conn.key) ??
            (conn.connectionId ? agentProviderByKey.get(conn.connectionId) : undefined)
          : undefined;
      return provider && FOUNDRY_AGENT_PROVIDERS.has(provider) ? 'foundry' : 'native';
    },
    [agentProviderByKey],
  );

  // Single-tab-stop listbox with an `aria-activedescendant` cursor tracks the
  // focused option (Arrow/Home/End/PageUp/PageDown move it; Enter/Space
  // select). `activeIndex` is scoped to the *current* pagination page — a
  // Down-arrow at the last row on page 1 stops there; the user pages forward
  // with PageDown or the footer arrows. This mirrors AppList / WorkflowList's
  // paged interaction model rather than treating page boundaries as seams.
  const listboxId = useId();
  const optionId = useCallback(
    (index: number) => `${listboxId}-opt-${index}`,
    [listboxId],
  );

  // Pagination over the search-filtered result. Same shared hook + footer the
  // AppList and WorkflowList pages use, so a user who's paged through the app
  // list already knows the affordance here. `resetDeps` bounces to page 1 when
  // the search query narrows/broadens the result set, matching the reset-on-
  // filter behavior of the shared usePagination contract.
  const pagination = usePagination<MergedConnection>({
    totalItems: filteredConnections.length,
    storageKey: PICKER_PAGE_SIZE_STORAGE_KEY,
    resetDeps: [searchQuery],
  });
  const pagedConnections = useMemo(
    () => pagination.paginate(filteredConnections),
    [pagination, filteredConnections],
  );

  // Row DOM refs, keyed by page-local index, so keyboard nav can scroll the
  // active option into the viewport (`connectionList` scrolls internally when
  // page size exceeds `LIST_VIEWPORT_ROWS`).
  const rowRefs = useRef<Array<HTMLDivElement | null>>([]);
  const setRowRef = useCallback((index: number) => (el: HTMLDivElement | null) => {
    rowRefs.current[index] = el;
  }, []);

  // Latest paged list + selection, read by the reset effects below without
  // making them dependencies (so they fire on user query/page changes only,
  // not on every unrelated list refetch or selection recording).
  const [activeIndex, setActiveIndex] = useState(() => {
    const selected = filteredConnections.findIndex((c) => c.key === currentConnectionName);
    return selected >= 0 ? selected : 0;
  });
  const pagedRef = useRef(pagedConnections);
  pagedRef.current = pagedConnections;
  const selectionRef = useRef(currentConnectionName);
  selectionRef.current = currentConnectionName;

  // When the *page* changes (either from search reset or user paging), seed
  // the active cursor to the selected connection if it's on this page, else 0.
  useEffect(() => {
    const selected = pagedRef.current.findIndex((c) => c.key === selectionRef.current);
    setActiveIndex(selected >= 0 ? selected : 0);
  }, [pagination.page, searchQuery]);

  // Clamp active index to the current page's row count so a shrinking result
  // (e.g. a deleted connection or a narrower search) can't leave the cursor
  // pointing past the last rendered option.
  useEffect(() => {
    setActiveIndex((i) => Math.min(i, Math.max(0, pagedConnections.length - 1)));
  }, [pagedConnections.length]);

  // Selecting a connection (keyboard Enter/Space, or a mouse click on a row)
  // also moves the active cursor to that row so subsequent arrow keys continue
  // from where the user acted instead of a stale index.
  const handleActivateConnection = useCallback(
    (referenceKey: string, index: number) => {
      setActiveIndex(index);
      handleSelectConnection(referenceKey);
    },
    [handleSelectConnection],
  );

  const handleListKeyDown = useCallback(
    (e: React.KeyboardEvent<HTMLDivElement>) => {
      const count = pagedConnections.length;
      if (count === 0) return;
      switch (e.key) {
        case 'ArrowDown':
          e.preventDefault();
          setActiveIndex((i) => {
            const next = i + 1;
            // Auto-advance to the next page when the cursor overflows the
            // last option — matches the "arrow keys traverse the full result"
            // mental model despite the paging boundary.
            if (next >= count && pagination.page < pagination.totalPages) {
              pagination.setPage(pagination.page + 1);
              return 0;
            }
            return Math.min(count - 1, next);
          });
          break;
        case 'ArrowUp':
          e.preventDefault();
          setActiveIndex((i) => {
            if (i === 0 && pagination.page > 1) {
              pagination.setPage(pagination.page - 1);
              return pagination.pageSize - 1;
            }
            return Math.max(0, i - 1);
          });
          break;
        case 'PageDown':
          e.preventDefault();
          if (pagination.page < pagination.totalPages) {
            pagination.setPage(pagination.page + 1);
            setActiveIndex(0);
          } else {
            setActiveIndex((i) => Math.min(count - 1, i + KEYBOARD_PAGE_STEP));
          }
          break;
        case 'PageUp':
          e.preventDefault();
          if (pagination.page > 1) {
            pagination.setPage(pagination.page - 1);
            setActiveIndex(0);
          } else {
            setActiveIndex((i) => Math.max(0, i - KEYBOARD_PAGE_STEP));
          }
          break;
        case 'Home':
          e.preventDefault();
          setActiveIndex(0);
          break;
        case 'End':
          e.preventDefault();
          setActiveIndex(count - 1);
          break;
        case 'Enter':
        case ' ': {
          e.preventDefault();
          const conn = pagedConnections[activeIndex];
          if (conn) handleActivateConnection(conn.key, activeIndex);
          break;
        }
        default:
          break;
      }
    },
    [pagedConnections, activeIndex, handleActivateConnection, pagination],
  );

  // Scroll the active option into view before paint so the element
  // `aria-activedescendant` points at is always visible after a keyboard jump
  // (Home/End/PageUp/PageDown) or a page transition that reseeds the cursor.
  useEffect(() => {
    const el = rowRefs.current[activeIndex];
    // jsdom (test env) doesn't implement scrollIntoView; guard so the effect
    // doesn't blow up any test that renders the picker.
    el?.scrollIntoView?.({ block: 'nearest' });
  }, [activeIndex, pagination.page]);

  // Connector display info now comes entirely from the connector manifest via
  // useWorkflowConnectors — nothing is persisted on the node metadata.
  const connectorIcon = connectorInfo?.iconUri;

  // Render creation panel
  if (showCreationPanel) {
    // The Microsoft-managed connection is a synthetic singleton, not something a
    // user manually configures — it has no connection-creation form. If the
    // node currently has Microsoft-managed selected (the common auto-selected default
    // when no BYOM connection exists yet — see the AgentTypeView variant short-circuit)
    // and the user explicitly clicks "Create new connection", they mean
    // "help me set up a real (BYOM) connection", not "recreate Microsoft-managed". Fall
    // back to AzureOpenAI so the form isn't opened on a provider with no
    // fields and a permanently-disabled Create button.
    const creationModelTypeSeed =
      agentModelType === MANAGED_MODEL_PROVIDER_ID ? 'AzureOpenAI' : agentModelType;
    const creationAgentModelType = agentVariant
      ? getAgentConnectionCreationModelType(creationModelTypeSeed, agentVariant)
      : undefined;
    const creationConnectorName = resolveCreationConnectorName({
      isAgentOperation,
      agentModelType: creationAgentModelType,
      connectorDisplayName: connectorInfo?.displayName,
      nodeType,
    });

    return (
      <div className={styles.container}>
        <ConnectionCreationPanel
          {...(connectorId ? { connectorId } : {})}
          connectorType={nodeType}
          {...(creationConnectorName ? { connectorName: creationConnectorName } : {})}
          {...(connectorIcon ? { connectorIcon } : {})}
          {...(connectorManifest ? { connectorManifest } : {})}
          {...(creationAgentModelType ? { agentModelType: creationAgentModelType } : {})}
          {...(agentVariant ? { agentVariant } : {})}
          onComplete={handleCreateComplete}
          onBack={() => setShowCreationPanel(false)}
        />
      </div>
    );
  }

  return (
    <div className={styles.container} data-testid="connection-tab">
      {/* Current Connection Card - Compact */}
      <div
        className={mergeClasses(
          styles.currentCard,
          statusInfo.status === 'connected' && styles.currentCardConnected,
          statusInfo.status === 'error' && styles.currentCardError,
          statusInfo.status === 'warning' && styles.currentCardWarning,
          statusInfo.status === 'mock' && styles.currentCardMock
        )}
        data-testid="connection-status-card"
      >
        <div className={styles.cardHeader}>
          {statusInfo.icon}
          <div className={styles.statusInfo}>
            <Text className={styles.statusTitle}>{statusInfo.title}</Text>
            <Text className={styles.statusSubtitle}>{statusInfo.subtitle}</Text>
          </div>
          {effectiveConnectionName && statusInfo.status !== 'mock' && (
            <div className={styles.headerActions}>
              <Button
                appearance="subtle"
                size="small"
                icon={testMutation.isPending ? <Spinner size="extra-tiny" /> : <ArrowSync16Regular />}
                onClick={handleTestConnection}
                disabled={testMutation.isPending}
                aria-label={testResult === 'success' ? 'Test passed' : testResult === 'error' ? 'Test failed' : 'Test connection'}
              />
            </div>
          )}
        </div>
      </div>

      {/* Connection List */}
      <div className={styles.listSection}>
        <div className={styles.listHeader}>
          <Text className={styles.listTitle}>
            Connections
          </Text>
          {filteredConnections.length > 0 && (
            <Badge appearance="tint" color="informative" size="small">
              {filteredConnections.length}
            </Badge>
          )}
        </div>

        <Input
          className={styles.searchInput}
          contentBefore={<Search16Regular />}
          placeholder="Search..."
          value={searchQuery}
          onChange={(_, data) => setSearchQuery(data.value)}
          appearance="filled-darker"
          size="small"
          data-testid="connection-search-input"
        />

        {/* Server-side truncation warning — surfaces the exact case the
            reviewer flagged: `listConnections` hit `CONNECTIONS_MAX_PAGES` and
            stopped with a live `nextLink` still present. Rendering this
            explicitly, above the paged list, keeps a large tenant from
            silently reading as a partial list. */}
        {connectionsMeta?.truncated && (
          <MessageBar intent="warning" data-testid="connections-truncated-warning">
            <MessageBarBody>
              Showing the first {connectionsMeta.fetchedPages} pages of connections
              (up to {connectionsMeta.maxPages} pages). Refine your search to see
              connections beyond this cap.
            </MessageBarBody>
          </MessageBar>
        )}

        {/* Create new - compact */}
        <div
          className={styles.createNewButton}
          onClick={() => setShowCreationPanel(true)}
          role="button"
          tabIndex={0}
          onKeyDown={(e) => {
            if (e.key === 'Enter' || e.key === ' ') {
              e.preventDefault();
              setShowCreationPanel(true);
            }
          }}
          aria-label="Create new connection"
          data-testid="create-new-connection-button"
        >
          <div className={styles.createNewIcon}>
            <Add16Regular />
          </div>
          <Text className={styles.createNewText}>
            Create new connection
          </Text>
        </div>

        {/* List */}
        {isLoadingConnections ? (
          <div className={styles.loadingContainer}>
            <Spinner size="tiny" />
          </div>
        ) : isConnectionsError ? (
          <div className={styles.errorContainer} role="alert">
            <MessageBar intent="error">
              <MessageBarBody>Couldn't load connections.</MessageBarBody>
              <MessageBarActions>
                <Button
                  size="small"
                  appearance="transparent"
                  onClick={() => refetchConnections()}
                  data-testid="connection-list-retry"
                >
                  Retry
                </Button>
              </MessageBarActions>
            </MessageBar>
          </div>
        ) : filteredConnections.length === 0 ? (
          <div className={styles.emptyState}>
            <PlugDisconnected16Regular className={styles.emptyIcon} />
            <Text className={styles.emptyText}>No connections found</Text>
          </div>
        ) : (
          <>
            <div
              className={styles.connectionList}
              role="listbox"
              aria-label="Available connections"
              aria-activedescendant={optionId(activeIndex)}
              tabIndex={0}
              onKeyDown={handleListKeyDown}
              data-testid="connection-list"
            >
              {pagedConnections.map((conn, index) => (
                <CompactConnectionItem
                  key={conn.key}
                  name={conn.name}
                  referenceKey={conn.key}
                  {...(conn.iconUri ? { iconUri: conn.iconUri } : {})}
                  isAI={isAgentOperation}
                  aiVariant={getAiVariant(conn)}
                  status="connected"
                  isSelected={currentConnectionName === conn.key}
                  onSelect={(key) => handleActivateConnection(key, index)}
                  id={optionId(index)}
                  focusable={false}
                  ref={setRowRef(index)}
                  {...(index === activeIndex ? { className: styles.itemActive } : {})}
                />
              ))}
            </div>
            {/* Only render the footer when there's more than one page — a
                short list doesn't need the ceremony. Uses the same shared
                component AppList / WorkflowList use. */}
            {filteredConnections.length > pagination.pageSize && (
              <div className={styles.paginationFooter}>
                <TablePaginationFooter
                  totalItems={filteredConnections.length}
                  currentPage={pagination.page}
                  pageSize={pagination.pageSize}
                  onPageChange={pagination.setPage}
                  onPageSizeChange={pagination.setPageSize}
                />
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
};

ConnectionTab.displayName = 'ConnectionTab';
